/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
/**
 * @author      david.loehr@zumper.com (David Loehr)
 * @copyright   Copyright (c) 2022, Zumper
 * @description CarouselV4 component
 */

import { useResizeObserver } from '@zcode-ui/hook'
import { ZumperLoader } from '@zcode-ui/loaders'
import React, {
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { flushSync } from 'react-dom'
import { twMerge } from 'tailwind-merge'
import { throttle } from 'throttle-debounce'
import './CarouselV4.css'
import { CarouselV4Button } from './CarouselV4Button'
import { CarouselV4Dots } from './CarouselV4Dots'

const isUnloadedImage = (
  anything: Element | null | undefined
): anything is HTMLImageElement => {
  return (
    typeof HTMLImageElement !== 'undefined' &&
    anything instanceof HTMLImageElement &&
    !anything.complete
  )
}

const isHTMLElement = (node: Node): node is HTMLElement =>
  node instanceof HTMLElement

const getElementChildren = (element: Element) =>
  Array.prototype.filter.call(element.children, isHTMLElement)

export interface CarouselV4Api {
  goToPreviousSlide: () => void
  goToNextSlide: () => void
  goToSlide: (index: number) => void
}

export interface CarouselV4Props {
  /** listen for left and right arrow key presses globally to go to previous/next slide */
  arrowKeys?: boolean
  carouselClassName?: string
  /**
   * add space before the first item (and after the last), so that that item appears
   * centered in the viewport when scrolled to the beginning/end
   */
  centerFirstAndLast?: boolean
  children: readonly ReactNode[]
  /** show dots at bottom center to indicate scroll position */
  dots?: boolean
  dotsCustomClassName?: string
  /** place dots in a gray pill, to ensure visibility against light backgrounds */
  dotsPill?: boolean
  gap?: number
  /**
   * hides the previous/next buttons
   * the buttons are always hidden on touch devices
   */
  hideButtons?: boolean
  /**
   * which item to scroll into view
   */
  initialIndex?: number
  /** show a loader if the current image is still loading */
  loader?: boolean
  /** when user clicks "next" on last slide, return to first slide */
  loop?: boolean
  /** show "4 / 8" at bottom center when scroll position changes */
  numbers?: boolean
  /** called with the 0-based index of the currently-in-view item */
  onChangeSlide?: (currentIndex: number) => void
}

export type CarouselV4Ref =
  | React.RefCallback<Readonly<CarouselV4Api> | null>
  | React.MutableRefObject<Readonly<CarouselV4Api> | null>
  | null

const EMPTY_OBJECT = {} as const

export const CarouselV4 = forwardRef(
  (
    {
      arrowKeys,
      carouselClassName,
      centerFirstAndLast,
      children,
      dots,
      dotsCustomClassName,
      dotsPill,
      gap,
      hideButtons,
      initialIndex,
      loader,
      loop,
      numbers,
      onChangeSlide,
      // @ts-expect-error
      sx: { _after, _before, ...sx } = EMPTY_OBJECT,
    }: CarouselV4Props,
    ref: CarouselV4Ref
  ) => {
    if (children.length < 2) {
      dots = false
      hideButtons = true
    }
    const haveSeenInitialIndex = useRef(!initialIndex)
    const needScrollListener =
      arrowKeys || dots || onChangeSlide || !!initialIndex
    const needRef = needScrollListener || !hideButtons || !!ref
    const offsetsLeftRef = useRef<readonly number[]>([])
    const [afterSpace, setAfterSpace] = useState<number | null>(null)
    const [beforeSpace, setBeforeSpace] = useState<number | null>(null)
    const [element, setElement] = useState<HTMLDivElement | null>(null)
    const [outerRef, setOuterRef] = useState<HTMLDivElement | null>(null)
    const [currentIndex, setCurrentIndex] = useState<number>(0)

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    const onScroll = useCallback(() => {
      if (element && offsetsLeftRef.current.length > 0) {
        const { offsetWidth, scrollLeft, scrollWidth } = element
        if (scrollLeft === 0) {
          setCurrentIndex(0)
        } else if (Math.abs(scrollWidth - offsetWidth - scrollLeft) < 2) {
          setCurrentIndex(offsetsLeftRef.current.length - 1)
          const children = getElementChildren(element)
          const lastChild = children[children.length - 1]
          setAfterSpace(
            offsetsLeftRef.current.length > 1
              ? Math.max(0, (offsetWidth - lastChild.offsetWidth) / 2)
              : 0
          )
        } else {
          const centerOfFrame = offsetWidth / 2 + scrollLeft
          const differences = offsetsLeftRef.current
            .slice(0, offsetsLeftRef.current.length - 1)
            .map((offset, index) => {
              const widthOfItem = offsetsLeftRef.current[index + 1] - offset
              const centerOfItem = offset + widthOfItem / 2
              return Math.abs(centerOfFrame - centerOfItem)
            })
          setCurrentIndex(
            Math.max(0, differences.indexOf(Math.min(...differences)))
          )
        }
      }
    }, [element, offsetsLeftRef])
    useEffect(() => {
      if (!haveSeenInitialIndex.current && currentIndex) {
        haveSeenInitialIndex.current = true
      }
      if (haveSeenInitialIndex.current) {
        onChangeSlide?.(currentIndex)
      }
    }, [currentIndex, onChangeSlide])
    useEffect(() => {
      if (element && !haveSeenInitialIndex.current) {
        const interval = setInterval(() => {
          if (haveSeenInitialIndex.current) {
            clearInterval(interval)
          }
          element?.dispatchEvent(new Event('scroll'))
        }, 25)
        return () => {
          clearInterval(interval)
        }
      }
    }, [element])

    useEffect(() => {
      if (element && needScrollListener) {
        const throttled = throttle(50, onScroll)
        element.addEventListener('scroll', throttled, { passive: true })
        return () => {
          element.removeEventListener('scroll', throttled)
        }
      }
    }, [element, onScroll, needScrollListener])

    const setOffsetsLeft = useCallback((element: Element) => {
      offsetsLeftRef.current = getElementChildren(element).map(
        (child) => child.offsetLeft
      )
      if (offsetsLeftRef.current.length < 2) {
        setBeforeSpace(0)
        setAfterSpace(0)
      } else if (element instanceof HTMLElement) {
        const { offsetWidth } = element
        const children = getElementChildren(element)
        const lastChild = children[children.length - 1]
        setAfterSpace(
          offsetsLeftRef.current.length > 1
            ? Math.max(0, (offsetWidth - lastChild.offsetWidth) / 2)
            : 0
        )
      }
    }, [])

    const computeBeforeSpace = useCallback(
      (
        target: EventTarget | null,
        element: HTMLDivElement,
        _width?: number
      ) => {
        if (
          target instanceof HTMLElement &&
          !isUnloadedImage(target) &&
          !Array.prototype.some.call(
            target.querySelectorAll('img'),
            isUnloadedImage
          )
        ) {
          const width = _width ?? target.offsetWidth
          setBeforeSpace(
            element.children.length > 1
              ? Math.max(0, (element.offsetWidth - width) / 2)
              : 0
          )
          return true
        }
        return false
      },
      []
    )

    const itemObserver = useResizeObserver(
      useCallback(
        function itemObserver(entries: ResizeObserverEntry[]) {
          if (element) {
            for (let i = entries.length - 1; i >= 0; i--) {
              const entry = entries[i]
              if (!entry.target.previousElementSibling) {
                const width = entry.borderBoxSize?.length
                  ? entry.borderBoxSize[0].inlineSize
                  : entry.contentRect.width
                if (!computeBeforeSpace(entry.target, element, width)) {
                  entry.target.addEventListener(
                    'load',
                    (e) => {
                      computeBeforeSpace(e.currentTarget, element)
                    },
                    { capture: true, once: true }
                  )
                }
                break
              }
            }
            flushSync(() => {
              setOffsetsLeft(element)
            })
            if (
              element.scrollLeft >
              element.scrollWidth - element.offsetWidth
            ) {
              element.scrollLeft = element.scrollWidth - element.offsetWidth
            }
          }
        },
        [computeBeforeSpace, element, setOffsetsLeft]
      )
    )

    const observer = useResizeObserver(
      useCallback(
        function observerCallback(entries: ResizeObserverEntry[]) {
          setOffsetsLeft(entries[0].target)
        },
        [setOffsetsLeft]
      )
    )
    useEffect(() => {
      if (element && itemObserver) {
        const observer = new MutationObserver((entries) => {
          for (const { addedNodes, removedNodes } of entries) {
            for (const node of removedNodes) {
              if (node instanceof Element) {
                itemObserver.unobserve(node)
              }
            }
            for (const node of addedNodes) {
              if (node instanceof Element) {
                itemObserver.observe(node, { box: 'border-box' })
              }
            }
            if (removedNodes.length && !addedNodes.length) {
              setOffsetsLeft(element)
            }
          }
        })
        for (const child of getElementChildren(element)) {
          itemObserver.observe(child, { box: 'border-box' })
        }
        observer.observe(element, { childList: true })
        return () => {
          observer.disconnect()
        }
      }
    }, [element, itemObserver, setOffsetsLeft])
    useEffect(() => {
      if (observer && element) {
        observer.observe(element)
        return () => {
          observer.unobserve(element)
        }
      }
    }, [element, observer])

    useEffect(() => {
      if (outerRef) {
        const preloadImages = () => {
          for (const img of outerRef.querySelectorAll<HTMLImageElement>(
            'img[loading="lazy"]'
          )) {
            img.setAttribute('loading', 'eager')
          }
        }

        outerRef.addEventListener('mouseover', preloadImages, {
          once: true,
          passive: true,
        })
        outerRef.addEventListener('touchstart', preloadImages, {
          once: true,
          passive: true,
        })
        return () => {
          outerRef.removeEventListener('mouseover', preloadImages)
          outerRef.removeEventListener('touchstart', preloadImages)
        }
      }
    }, [outerRef])
    const goToSlide = useMemo(
      () =>
        element
          ? (index: number, disableScrollBehavior?: boolean) => {
              const target = element.children.item(index)
              if (target instanceof HTMLElement) {
                const { offsetWidth, scrollWidth } = element
                let left: number
                if (index === element.children.length - 1) {
                  left = scrollWidth - offsetWidth // scroll to the end
                } else {
                  const { offsetLeft, offsetWidth: widthOfItem } = target
                  const centerOfItem = offsetLeft + widthOfItem / 2
                  left = Math.max(0, centerOfItem - offsetWidth / 2)
                }
                const options: ScrollToOptions = { left }
                // Safari doesn't seem to work when scrollTo runs with "behavior" on mount
                // but it works on subsequent runs, so we selectively disable it for the first run only.
                if (!disableScrollBehavior) {
                  options.behavior = 'smooth'
                }
                element?.scrollTo(options)
              }
            }
          : undefined,
      [element]
    )
    const goToPreviousSlide = useCallback(
      (e?: React.MouseEvent<HTMLButtonElement>) => {
        e?.stopPropagation()
        goToSlide?.(currentIndex ? currentIndex - 1 : children.length - 1)
      },
      [children.length, currentIndex, goToSlide]
    )
    const goToNextSlide = useCallback(
      (e?: React.MouseEvent<HTMLButtonElement>) => {
        e?.stopPropagation()
        goToSlide?.(currentIndex < children.length - 1 ? currentIndex + 1 : 0)
      },
      [children.length, currentIndex, goToSlide]
    )
    const [showLoader, setShowLoader] = useState(false)
    useEffect(() => {
      const child = outerRef?.firstElementChild?.children.item(currentIndex)
      if (loader) {
        if (isUnloadedImage(child)) {
          setShowLoader(true)
          const hide = () => setShowLoader(false)
          child.addEventListener('load', hide, { once: true })
          child.addEventListener('error', hide, { once: true })
          return () => {
            child.removeEventListener('error', hide)
            child.removeEventListener('load', hide)
          }
        }
        setShowLoader(false)
      }
    }, [currentIndex, loader, outerRef])
    const [api, setApi] = useState<CarouselV4Api | null>(null)
    useEffect(() => {
      setApi(
        goToSlide
          ? {
              goToNextSlide,
              goToPreviousSlide,
              goToSlide,
            }
          : null
      )
    }, [goToSlide])
    useEffect(() => {
      if (api) {
        api.goToNextSlide = goToNextSlide
        api.goToPreviousSlide = goToPreviousSlide
      }
    }, [api, goToNextSlide, goToPreviousSlide])
    useEffect(() => {
      if (api) {
        if (typeof ref === 'function') {
          ref(api)
          return () => {
            ref(null)
          }
        }
        if (typeof ref === 'object' && ref !== null) {
          ref.current = api
          return () => {
            ref.current = null
          }
        }
      }
    }, [api, ref])
    useEffect(() => {
      if (initialIndex && goToSlide) {
        goToSlide(initialIndex, true)
      }
    }, [goToSlide, initialIndex])

    const onKeyDown = useMemo(
      () =>
        api
          ? (e: KeyboardEvent | React.KeyboardEvent) => {
              if (
                e.target &&
                'nodeName' in e.target &&
                typeof e.target.nodeName === 'string' &&
                (e.target.nodeName === 'INPUT' ||
                  e.target.nodeName === 'TEXTAREA')
              ) {
                return
              }
              if (e.key === 'ArrowLeft') {
                e.preventDefault()
                api?.goToPreviousSlide()
              }
              if (e.key === 'ArrowRight') {
                e.preventDefault()
                api?.goToNextSlide()
              }
            }
          : undefined,
      [api]
    )

    useEffect(() => {
      if (arrowKeys && onKeyDown) {
        window.addEventListener('keydown', onKeyDown)
        return () => {
          window.removeEventListener('keydown', onKeyDown)
        }
      }
    }, [arrowKeys, onKeyDown])

    return (
      <div
        className={twMerge(
          'size-full max-h-full max-w-full relative',
          centerFirstAndLast && beforeSpace == null ? 'invisible' : 'visible'
        )}
        ref={setOuterRef}
      >
        <div
          className={twMerge(
            'hide-scrollbar h-full max-h-full overflow-x-auto overflow-y-hidden overflow-anchor-none snap-x snap-mandatory overflow-anchor-none scrollbar-none whitespace-nowrap',
            centerFirstAndLast && beforeSpace != null && 'before-space',
            centerFirstAndLast && afterSpace != null && 'after-space',
            carouselClassName
          )}
          style={
            {
              '--after-space': `${afterSpace}px`,
              '--before-space': `${beforeSpace}px`,
              '--gap': `${gap}px`,
            } as React.CSSProperties
          }
          ref={needRef ? setElement : undefined}
        >
          {children}
        </div>
        {showLoader && (
          <ZumperLoader containerClassName="absolute top-[25%] right-[25%] bottom-[25%] left-[25%] [&>div]:size-full [&>div>svg]:size-full" />
        )}
        {!hideButtons && (
          <>
            {(currentIndex !== 0 || loop) && (
              <CarouselV4Button
                onClick={goToPreviousSlide}
                onKeyDown={onKeyDown}
                type="previous"
              />
            )}
            {(currentIndex !== children.length - 1 || loop) && (
              <CarouselV4Button
                onClick={goToNextSlide}
                onKeyDown={onKeyDown}
                type="next"
              />
            )}
          </>
        )}
        {dots && (
          <CarouselV4Dots
            currentSlide={currentIndex}
            dotsCustomClassName={dotsCustomClassName}
            goToSlide={goToSlide}
            inPill={dotsPill}
            onKeyDown={onKeyDown}
            showNumbers={numbers}
            totalItems={children.length}
          />
        )}
      </div>
    )
  }
)
